import React, { useState } from "react";

import { Person } from "@mui/icons-material";
import HeaderButton from "./HeaderButton";
import { SxProps, Theme, useTheme } from "@mui/material/styles";
import { RouteNameToMuiIconMapper } from "../../Helpers/IconSrcToMuiMapper/RouteNameToMuiIconMapper";
import { BNGoogleTagManager, getRouteAction, resolveUrl } from "../../Helpers/helpers";
import BNDrawer from "../BNDialog/BNDrawer";
import BNListItem from "../BNListItem/BNListItem";
import { NavigationLink } from "../NavigationLink/NavigationLink";
import { DialogContent, Divider } from "@mui/material";
import { BNHeaderProps, BNHeaderStrings, UserNavigationLinks } from "./BNHeader";
import BNStack from "../BNStack/BNStack";
import BNButton from "../FormElements/BNButton/BNButton";
//import { PublicPortalDataLayerEvent } from "../../Helpers/GoogleTagManager/events/PublicPortalDataLayerEvent";
import { UiElementClickEvent } from "../../Helpers/GoogleTagManager/UiElementClickEvent";
import { EventAction, EventLabel } from "../../Helpers/GoogleTagManager/BNGoogleTagManager";
import BNThemeSelector from "../BNThemeSelector/BNThemeSelector";

interface Props {
  onNavigate?: (title: string) => void;
  userNavigationLinks?: UserNavigationLinks;
  settingsLinks: NavigationLink[];
  text: string;
  isSpa: (path: string) => boolean;
  openMenuIconButtonSx?: SxProps<Theme>;
  strings: BNHeaderStrings | undefined;
}

const AccountSettings: React.FC<Props> = ({
  settingsLinks,
  userNavigationLinks,
  onNavigate,
  isSpa,
  text,
  openMenuIconButtonSx,
}) => {
  const theme = useTheme();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const privacyPolicyLink = userNavigationLinks?.privacyPolicy;
  const cookiePolicyLink = userNavigationLinks?.cookiePolicy;
  const logoutLink = userNavigationLinks?.logout;

  const onNavigateHandler = (title: string) => () => {
    setIsOpen(false);
    if (onNavigate) {
      onNavigate(title);
    }
  };

  return (
    <>
      <HeaderButton
        dataUit={"user-button"}
        text={text}
        icon={Person}
        ariaLabel={"Menu"}
        iconButtonSx={openMenuIconButtonSx}
        onClick={() => setIsOpen(true)}
      />
      <BNDrawer title={text} open={isOpen} onClose={() => setIsOpen(false)} fullWidthContent fullHeight important>
        <DialogContent sx={{ px: 3 }}>
          <BNThemeSelector />
          {logoutLink && (
            <BNStack spacing={1} sx={{ mx: "auto", mt: 2 }}>
              <BNListItem
                isSpa={isSpa}
                to={logoutLink.url}
                icon={
                  <RouteNameToMuiIconMapper
                    routeName={logoutLink.routeName}
                    consumer={`component=InPageNavigation text=${logoutLink.title}`}
                  />
                }
                data-uit={getRouteAction(logoutLink.url)}
                sx={[
                  { py: 2, px: { xs: 2, md: 3 }, backgroundColor: theme.vars.palette.secondary.lighter },
                  theme.applyStyles("dark", {
                    backgroundColor: theme.vars.palette.background.default,
                  }),
                ]}
                textSx={{ fontWeight: "normal" }}
                onClick={onNavigateHandler(logoutLink.title)}
              >
                {logoutLink.title}
              </BNListItem>
            </BNStack>
          )}
          <BNStack
            spacing={0}
            divider={<Divider sx={{ borderColor: theme.vars.palette.background.paper }} />}
            sx={{ mx: "auto", mt: 2, borderRadius: "0.8rem", overflow: "hidden" }}
          >
            {settingsLinks
              .filter((x) => !!x)
              .map((x) => (
                <BNListItem
                  key={x.url}
                  isSpa={isSpa}
                  to={x.url}
                  icon={
                    <RouteNameToMuiIconMapper
                      routeName={x.routeName}
                      consumer={`component=InPageNavigation text=${x.title}`}
                    />
                  }
                  data-uit={getRouteAction(x.url)}
                  sx={[
                    {
                      py: 2,
                      px: { xs: 2, md: 3 },
                      borderRadius: 0,
                      backgroundColor: theme.vars.palette.secondary.lighter,
                    },
                    theme.applyStyles("dark", {
                      backgroundColor: theme.vars.palette.background.default,
                    }),
                  ]}
                  textSx={{ fontWeight: "normal" }}
                  onClick={onNavigateHandler(x.title)}
                >
                  {x.title}
                </BNListItem>
              ))}
          </BNStack>
          <BNStack
            direction={"row"}
            spacing={2}
            sx={{
              mt: 1,
            }}
          >
            {privacyPolicyLink && (
              <BNButton
                variant="text"
                sx={{ px: 0 }}
                href={resolveUrl(privacyPolicyLink.url)}
                onClick={onNavigateHandler(privacyPolicyLink.title)}
              >
                {privacyPolicyLink.title}
              </BNButton>
            )}
            {cookiePolicyLink && (
              <BNButton
                variant="text"
                sx={{ px: 0 }}
                href={resolveUrl(cookiePolicyLink.url)}
                onClick={onNavigateHandler(cookiePolicyLink.title)}
              >
                {cookiePolicyLink.title}
              </BNButton>
            )}
          </BNStack>
        </DialogContent>
      </BNDrawer>
    </>
  );
};

const AccountSettingsWrapper: React.FC<{
  settingsLinks: NavigationLink[];
  userNavigationLinks?: UserNavigationLinks;
  openMenuIconButtonSx?: SxProps<Theme>;
  isSpa: BNHeaderProps["isSpa"];
  strings: BNHeaderStrings | undefined;
  accountText?: string;
}> = ({ settingsLinks, userNavigationLinks, openMenuIconButtonSx, isSpa, strings, accountText }) => (
  <AccountSettings
    text={strings?.Account || accountText || ""}
    strings={strings}
    userNavigationLinks={userNavigationLinks}
    settingsLinks={settingsLinks}
    onNavigate={(title) => {
      BNGoogleTagManager.addEventToDataLayer(
        new UiElementClickEvent(EventAction.Header, EventLabel.Settings + ":" + title),
      );
    }}
    isSpa={isSpa}
    openMenuIconButtonSx={openMenuIconButtonSx}
  />
);

export default AccountSettingsWrapper;
